/**
 * The four areas — Strategy, Priorities, Plans, Action — shared by both shells.
 *
 * Strategy = strategic intent. Priorities = what matters / ranking work.
 * Plans = preparing the next plan. Action = the plan of record in force, and
 * the monitoring and changes that come with living it out (src/lib/appAreas.ts).
 *
 * Settings lives under `/prioritization/settings/*` for every area and keeps
 * the area you opened it from.
 */

import { Link, useLocation } from 'react-router-dom';
import { Activity, ListOrdered, Map, TreeDeciduous } from 'lucide-react';
import { setMandateShellActive, setSettingsEntryProduct } from '../../lib/productSurfacePaths';
import { useSettingsEntryProduct } from '../../hooks/useSettingsEntryProduct';
import { AREA_HOME, AREA_LABEL, AREA_TITLE, rememberArea, useSelectedArea, type AppArea } from '../../lib/appAreas';
import { useModuleGate } from '../../lib/moduleGate';

const AREAS: { area: AppArea; icon: typeof Map }[] = [
  { area: 'strategy', icon: TreeDeciduous },
  { area: 'priorities', icon: ListOrdered },
  { area: 'plans', icon: Map },
  { area: 'action', icon: Activity },
];

// Four separate square buttons (Square/Mono): the chosen one is solid slate, the others outlined.
const segmentBase = 'flex h-9 items-center justify-center border font-mono text-[0.72rem] uppercase tracking-[0.08em] transition-colors';
const segmentActive = `${segmentBase} border-[var(--slate)] bg-[var(--slate)] text-white`;
const segmentIdle = `${segmentBase} border-[var(--rule-bright)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]`;

export interface ProductModeSwitcherProps {
  /** @deprecated PoR is no longer a top-level mode; kept for call-site compat. */
  alwaysShowPor?: boolean;
  className?: string;
  /** Called after the user picks a product surface (e.g. close the mobile drawer). */
  onNavigate?: () => void;
  /** Icon-rail sidebar: stacked icon buttons instead of labeled segments. */
  collapsed?: boolean;
  /** Areas this space leaves out (a personal or team space has no Strategy). */
  hide?: AppArea[];
}

export function ProductModeSwitcher({
  className = '',
  onNavigate,
  collapsed = false,
  hide = [],
}: ProductModeSwitcherProps = {}) {
  const location = useLocation();
  // Subscribe so the segment re-renders when an area opens Settings.
  useSettingsEntryProduct();
  // None is marked on Home (and other shared pages) until an area is chosen in this tab.
  const current = useSelectedArea(location.pathname);
  // Plans and Action both open into the Planning app, which is the `planning`
  // module: with it off for this person they are not drawn (§2.7, §2.8).
  const planningOn = useModuleGate('planning');
  const areas = AREAS.filter(
    (a) => !hide.includes(a.area) && (planningOn || (a.area !== 'plans' && a.area !== 'action')),
  );

  const go = (area: AppArea) => {
    rememberArea(area);
    if (area === 'plans' || area === 'action') {
      setSettingsEntryProduct(null);
    } else {
      setMandateShellActive(false);
      setSettingsEntryProduct('prioritization');
    }
    onNavigate?.();
  };

  if (collapsed) {
    return (
      <div className={`shrink-0 flex flex-col items-center gap-1 px-1.5 pt-3 pb-2 border-b border-[var(--rule)] ${className}`}>
        {areas.map(({ area, icon: Icon }) => (
          <Link
            key={area}
            to={AREA_HOME[area]}
            title={`${AREA_LABEL[area]} — ${AREA_TITLE[area]}`}
            aria-label={AREA_LABEL[area]}
            aria-current={current === area ? 'page' : undefined}
            onClick={() => go(area)}
            className={`w-9 ${current === area ? segmentActive : segmentIdle}`}
          >
            <Icon className="h-4 w-4" />
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className={`shrink-0 px-3 pt-3 pb-2 border-b border-[var(--rule)] ${className}`}>
      <nav aria-label="Areas" className={`grid gap-1.5 ${areas.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {areas.map(({ area }) => (
          <Link
            key={area}
            to={AREA_HOME[area]}
            title={AREA_TITLE[area]}
            aria-current={current === area ? 'page' : undefined}
            onClick={() => go(area)}
            className={`whitespace-nowrap ${current === area ? segmentActive : segmentIdle}`}
          >
            {AREA_LABEL[area]}
          </Link>
        ))}
      </nav>
    </div>
  );
}
